/**
 * OsItemHistoryDrawer — MADE-293
 *
 * Slide-in drawer (from right) showing the full inventory_events timeline for an
 * item across all sources (direct ops and count sessions), most recent first.
 *
 * GET /api/os/audit/sessions/:sessionId/items/:itemNumber/history
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { authFetch } from '../../madebb/utils/authFetch';
import { useTenant } from '../../contexts/TenantContext';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface InventoryEvent {
  eventId: string;
  operationType: string | null;  // legacy events may have no type
  source: 'count_session' | 'direct';
  sessionId?: string;
  delta: number;
  qtyBefore: number;
  qtyAfter: number;
  location: string;
  performedBy: string;
  performedAt: string;
}

interface Props {
  open: boolean;
  sessionId: string;
  itemNumber: string;
  itemDescription: string;
  onClose: () => void;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function fmtOpType(type: string | null | undefined): string {
  if (!type) return 'Inventory Event';
  return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Component ──────────────────────────────────────────────────────────────────

export const OsItemHistoryDrawer: React.FC<Props> = ({
  open,
  sessionId,
  itemNumber,
  itemDescription,
  onClose,
}) => {
  const { apiBase } = useTenant();
  const [events, setEvents] = useState<InventoryEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !itemNumber) return;
    setLoading(true);
    setError(null);
    authFetch(
      `${apiBase}/audit/sessions/${encodeURIComponent(sessionId)}/items/${encodeURIComponent(itemNumber)}/history`,
    )
      .then(async (res) => {
        if (!res.ok) throw new Error(`Failed to load history (${res.status})`);
        const data = await res.json();
        setEvents(data.events ?? []);
      })
      .catch((err) =>
        setError(err instanceof Error ? err.message : 'Failed to load history'),
      )
      .finally(() => setLoading(false));
  }, [open, sessionId, itemNumber, apiBase]);

  if (renderError) {
    return (
      <Drawer open={open} onClose={onClose} anchor="right">
        <Box sx={{ p: 3, width: 360 }}>
          <Typography color="error" variant="body2">
            Could not render history: {renderError}
          </Typography>
          <Button onClick={() => { setRenderError(null); setEvents([]); }} sx={{ mt: 2 }}>
            Dismiss
          </Button>
        </Box>
      </Drawer>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      data-testid="history-drawer"
      PaperProps={{ sx: { width: { xs: '100%', sm: 400 }, p: 2 } }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Item History
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {itemDescription} · #{itemNumber}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="Close history drawer"
          data-testid="close-history-drawer"
        >
          <CloseIcon />
        </IconButton>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {/* Loading */}
      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress data-testid="history-loading" />
        </Box>
      )}

      {/* Error */}
      {error && (
        <Typography color="error" data-testid="history-error">
          {error}
        </Typography>
      )}

      {/* Empty */}
      {!loading && !error && events.length === 0 && (
        <Typography color="text.secondary" data-testid="history-empty">
          No events found.
        </Typography>
      )}

      {/* Event list — most recent first (API returns them in descending order) */}
      {!loading && !error && events.length > 0 && (
        <Stack spacing={2} data-testid="history-event-list">
          {events.map((evt, idx) => (
            <Box key={evt.eventId} data-testid={`history-event-${idx}`}>
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Chip
                    label={fmtOpType(evt.operationType ?? null)}
                    size="small"
                    color={evt.source === 'count_session' ? 'primary' : 'default'}
                    variant="outlined"
                    data-testid={`event-type-chip-${idx}`}
                  />
                  <Typography variant="caption" color="text.secondary" data-testid={`event-time-${idx}`}>
                    {fmtDateTime(evt.performedAt)}
                  </Typography>
                </Stack>

                <Typography variant="body2" data-testid={`event-qty-${idx}`}>
                  {evt.qtyBefore} → {evt.qtyAfter}
                  {evt.delta !== 0 && (
                    <Box
                      component="span"
                      sx={{
                        ml: 0.5,
                        color: evt.delta > 0 ? 'success.main' : 'error.main',
                      }}
                    >
                      ({evt.delta > 0 ? '+' : ''}{evt.delta})
                    </Box>
                  )}
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  {evt.location} · {evt.performedBy}
                </Typography>
              </Stack>

              {idx < events.length - 1 && <Divider sx={{ mt: 1.5 }} />}
            </Box>
          ))}
        </Stack>
      )}
    </Drawer>
  );
};
